import React from "react"
import AttentionIndicator from "./AttentionIndicator"

// Student avatar colors - warm gradient palette
const avatarColors = [
  "from-violet-500 to-purple-600",
  "from-cyan-500 to-blue-600",
  "from-fuchsia-500 to-pink-600",
  "from-amber-500 to-orange-600",
  "from-emerald-500 to-teal-600",
  "from-rose-500 to-red-600",
]

const StudentOverviewCard = ({ student, isSelected, onClick }) => {
  const { stats, attention_score } = student

  // Get consistent color based on student ID
  const avatarColor = avatarColors[(student.id || 0) % avatarColors.length]

  const getQuickStatus = () => {
    if (stats.overdue > 0) {
      return { text: `${stats.overdue} overdue`, color: "text-red-600", bg: "bg-red-50" }
    }
    if (stats.due_today > 0) {
      return { text: `${stats.due_today} due today`, color: "text-amber-600", bg: "bg-amber-50" }
    }
    if (stats.due_this_week > 0) {
      return { text: `${stats.due_this_week} due this week`, color: "text-blue-600", bg: "bg-blue-50" }
    }
    return { text: "All caught up", color: "text-emerald-600", bg: "bg-emerald-50" }
  }

  const status = getQuickStatus()

  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left p-4 rounded-2xl transition-all duration-200
        hover-lift group
        ${isSelected
          ? "bg-[var(--ink)] text-white shadow-lg ring-2 ring-[var(--gold)]"
          : "bg-white shadow-sm hover:shadow-md"
        }
      `}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div className={`
            w-12 h-12 rounded-xl bg-gradient-to-br ${avatarColor}
            flex items-center justify-center text-white font-display font-bold text-lg
            shadow-sm transition-transform duration-200 group-hover:scale-105
          `}>
            {student.name?.charAt(0)?.toUpperCase() || student.email?.charAt(0)?.toUpperCase()}
          </div>
          <div className="absolute -bottom-1 -right-1">
            <AttentionIndicator score={attention_score} size="md" />
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold truncate ${isSelected ? "text-white" : "text-[var(--ink)]"}`}>
            {student.name}
          </h3>
          <p className={`text-sm truncate ${isSelected ? "text-white/70" : "text-[var(--ink-light)]"}`}>
            {student.email}
          </p>

          <div className="mt-2">
            <span className={`
              inline-flex items-center gap-1.5 text-sm font-medium px-2 py-0.5 rounded-lg
              ${isSelected ? "bg-white/10 text-white/90" : `${status.bg} ${status.color}`}
            `}>
              {stats.overdue > 0 && (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
              {status.text}
            </span>
          </div>
        </div>

        {/* Arrow indicator */}
        <svg
          className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${
            isSelected ? "text-white/60 rotate-90" : "text-[var(--ink-light)] group-hover:translate-x-0.5"
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>

      {/* Mini Stats Grid */}
      <div className="mt-4 grid grid-cols-4 gap-2">
        <div className={`rounded-xl py-2 px-1 text-center transition-colors ${
          isSelected
            ? "bg-white/10"
            : stats.overdue > 0 ? "bg-red-50" : "bg-[var(--cream)]"
        }`}>
          <span className={`text-sm font-bold block ${
            isSelected
              ? "text-white"
              : stats.overdue > 0 ? "text-red-600" : "text-[var(--ink-light)]"
          }`}>
            {stats.overdue}
          </span>
          <p className={`text-xs ${isSelected ? "text-white/60" : "text-[var(--ink-light)]"}`}>
            Overdue
          </p>
        </div>

        <div className={`rounded-xl py-2 px-1 text-center transition-colors ${
          isSelected
            ? "bg-white/10"
            : stats.due_today > 0 ? "bg-amber-50" : "bg-[var(--cream)]"
        }`}>
          <span className={`text-sm font-bold block ${
            isSelected
              ? "text-white"
              : stats.due_today > 0 ? "text-amber-600" : "text-[var(--ink-light)]"
          }`}>
            {stats.due_today}
          </span>
          <p className={`text-xs ${isSelected ? "text-white/60" : "text-[var(--ink-light)]"}`}>
            Today
          </p>
        </div>

        <div className={`rounded-xl py-2 px-1 text-center transition-colors ${
          isSelected
            ? "bg-white/10"
            : stats.due_this_week > 0 ? "bg-blue-50" : "bg-[var(--cream)]"
        }`}>
          <span className={`text-sm font-bold block ${
            isSelected
              ? "text-white"
              : stats.due_this_week > 0 ? "text-blue-600" : "text-[var(--ink-muted)]"
          }`}>
            {stats.due_this_week}
          </span>
          <p className={`text-xs ${isSelected ? "text-white/60" : "text-[var(--ink-light)]"}`}>
            Week
          </p>
        </div>

        <div className={`rounded-xl py-2 px-1 text-center transition-colors ${
          isSelected
            ? "bg-white/10"
            : stats.recently_graded > 0 ? "bg-violet-50" : "bg-[var(--cream)]"
        }`}>
          <span className={`text-sm font-bold block ${
            isSelected
              ? "text-white"
              : stats.recently_graded > 0 ? "text-violet-600" : "text-[var(--ink-muted)]"
          }`}>
            {stats.recently_graded}
          </span>
          <p className={`text-xs ${isSelected ? "text-white/60" : "text-[var(--ink-light)]"}`}>
            Graded
          </p>
        </div>
      </div>
    </button>
  )
}

export default StudentOverviewCard
